<template>
  <div class="aq-page">
    <!-- Topbar -->
    <div class="topbar">
      <div class="topbar-left">
        <button class="icon-btn back-btn" aria-label="Back" @click="$router.back()">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <span class="topbar-title">Inventory View</span>
      </div>
      <div class="topbar-right">
        <span v-if="lastRefreshed" style="color:rgba(255,255,255,.6); font-size:11px;">
          Last refreshed: {{ lastRefreshed.toLocaleTimeString() }}
        </span>
        <button class="icon-btn btn-save btn-pdf" @click="refresh">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right:6px"><path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
          Refresh
        </button>
      </div>
    </div>

    <div class="page-content">
      <div v-if="loadError" class="revision-bar" style="background:#fef2f2;border-color:#fecaca;">
        <span class="rev-label" style="color:#b91c1c;">Error</span>
        <span style="font-size:12px;color:#991b1b;">Something went wrong loading inventory data.</span>
        <button class="manage-btn" style="background:#b91c1c;margin-left:auto;" @click="refresh">Retry</button>
      </div>

      <!-- Search + filters -->
      <div class="filter-row">
        <div class="app-input" style="flex:1;min-width:240px;">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input class="app-input-field" v-model="searchTerm" placeholder="Search Item Code, Description, Group, Category..." />
        </div>

        <div class="app-input" style="max-width:150px;">
          <input class="app-input-field" v-model="filters.itemCode" placeholder="Item Code" />
        </div>
        <div class="app-input" style="max-width:170px;">
          <input class="app-input-field" v-model="filters.itemGroup" placeholder="Item Group" />
        </div>

        <div class="filter-select">
          <select class="native-select" v-model="filters.bom">
            <option value="all">All items</option>
            <option value="bom">Manufactured / BOM</option>
            <option value="non_bom">Stocked complete</option>
          </select>
        </div>

        <div class="filter-select">
          <select class="native-select" v-model="filters.qtyAvailable.op">
            <option value="none">Qty available: any</option>
            <option value="equals">Equals</option>
            <option value="gt">Greater than</option>
            <option value="lt">Less than</option>
            <option value="between">Between</option>
            <option value="zero">Zero</option>
            <option value="gt_zero">Greater than zero</option>
          </select>
        </div>
        <input
          v-if="['equals', 'gt', 'lt', 'between'].includes(filters.qtyAvailable.op)"
          v-model="filters.qtyAvailable.value" type="number" class="app-input-field"
          style="width:70px;border:1.5px solid #e2e8f0;border-radius:8px;padding:8px;"
        />
        <input
          v-if="filters.qtyAvailable.op === 'between'"
          v-model="filters.qtyAvailable.value2" type="number" class="app-input-field"
          style="width:70px;border:1.5px solid #e2e8f0;border-radius:8px;padding:8px;"
        />

        <button class="back-link" style="margin-left:auto;" @click="resetFilters">
          Clear all filters
        </button>
      </div>

      <!-- Table (Added main-table class for fixed header) -->
      <div class="tbl-card main-table">
        <div class="tbl-head">
          <span class="th" style="width:32px;"></span>
          <span class="th sortable" style="flex:1.2;" @click="setSort('itemCode')">Item Code <SortMark k="itemCode" :sort="sort" /></span>
          <span class="th sortable" style="flex:2;" @click="setSort('description')">Description <SortMark k="description" :sort="sort" /></span>
          <span class="th" style="flex:1.4;">Description 2</span>
          <span class="th sortable" style="flex:1;" @click="setSort('itemGroup')">Item Group <SortMark k="itemGroup" :sort="sort" /></span>
          
          <!-- ITEM CATEGORY IN MAIN TABLE ONLY -->
          <span class="th sortable" style="flex:1;" @click="setSort('itemCategory')">Item Category <SortMark k="itemCategory" :sort="sort" /></span>

          <span class="th sortable" style="width:90px;text-align:right;" @click="setSort('qtyAvailable')">Qty Avail <SortMark k="qtyAvailable" :sort="sort" /></span>
          <span class="th sortable" style="width:90px;text-align:right;" @click="setSort('qtyOnHand')">Qty Hand <SortMark k="qtyOnHand" :sort="sort" /></span>
          <span class="th sortable" style="width:90px;text-align:right;" @click="setSort('reservedQty')">Qty Res <SortMark k="reservedQty" :sort="sort" /></span>
          <span class="th sortable" style="width:90px;text-align:right;" @click="setSort('qtyOnPurchaseOrder')">Qty On P/O <SortMark k="qtyOnPurchaseOrder" :sort="sort" /></span>
          <span class="th sortable" style="width:90px;text-align:right;" @click="setSort('minimumLevel')">Min Lvl <SortMark k="minimumLevel" :sort="sort" /></span>
          <span class="th sortable" style="width:90px;text-align:right;" @click="setSort('maximumLevel')">Max Lvl <SortMark k="maximumLevel" :sort="sort" /></span>
          <span v-if="canViewCost" class="th sortable" style="width:100px;text-align:right;" @click="setSort('averageUnitCost')">Avg Cost <SortMark k="averageUnitCost" :sort="sort" /></span>
        </div>

        <div v-if="loading" class="empty-state">Loading inventory...</div>
        <div v-else-if="!items.length" class="empty-state">No inventory items are currently available.</div>
        <div v-else-if="!paged.length" class="empty-state">No inventory items match the selected search and filters.</div>

        <template v-else v-for="(item, i) in paged">
          
          <div :key="'row-' + item.id" class="tbl-row" :class="{ 'tbl-row-alt': i % 2 === 1, 'tbl-row-approved': expandedId === item.id }">
            <span style="width:32px;text-align:center;">
              <button
                v-if="item.hasBom"
                class="apr-btn"
                style="width:26px;height:26px;border-radius:6px;"
                :aria-label="expandedId === item.id ? 'Hide BOM components' : 'View BOM components'"
                title="View BOM components"
                @click="toggleBom(item)"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6d28d9" stroke-width="2">
                  <path v-if="expandedId === item.id" d="M18 15l-6-6-6 6"/>
                  <path v-else d="M3 21V9l9-6 9 6v12M9 21V12h6v9"/>
                </svg>
              </button>
            </span>
            <span style="flex:1.2;font-family:monospace;font-size:11px;">{{ item.itemCode }}</span>
            <span style="flex:2;font-size:12px;color:#1e293b;">{{ item.description }}</span>
            <span style="flex:1.4;font-size:12px;color:#64748b;">{{ item.description2 }}</span>
            <span style="flex:1;font-size:12px;">{{ item.itemGroup }}</span>
            
            <!-- ITEM CATEGORY DATA CELL IN MAIN TABLE ONLY -->
            <span style="flex:1;font-size:12px;">{{ item.itemCategory }}</span>

            <span style="width:90px;text-align:right;font-size:12px;font-weight:700;">{{ formatQty(item.qtyAvailable) }}</span>
            <span style="width:90px;text-align:right;font-size:12px;">{{ formatQty(item.qtyOnHand) }}</span>
            <span style="width:90px;text-align:right;font-size:12px;">{{ formatQty(item.reservedQty) }}</span>
            <span style="width:90px;text-align:right;font-size:12px;">{{ formatQty(item.qtyOnPurchaseOrder) }}</span>
            <span style="width:90px;text-align:right;font-size:12px;">{{ formatQty(item.minimumLevel) }}</span>
            <span style="width:90px;text-align:right;font-size:12px;">{{ formatQty(item.maximumLevel) }}</span>
            <span v-if="canViewCost" style="width:100px;text-align:right;font-size:12px;">{{ formatCurrency(item.averageUnitCost) }}</span>
          </div>

          <!-- Expanded BOM mini table (No Item Category here) -->
          <div :key="'bom-' + item.id" v-if="expandedId === item.id" style="border-left:3px solid #7c3aed;background:#faf9ff;padding:12px 16px;border-bottom:1px solid #f1f5f9;">
            <div v-if="bomLoading" style="font-size:12px;color:#64748b;">Loading BOM components...</div>

            <template v-else-if="expandedBom">
              <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;flex-wrap:wrap;gap:6px;">
                <span style="font-size:12px;font-weight:700;color:#1e293b;">{{ expandedBom.billDescription }}</span>
                <span class="bom-pill">
                  Estimated Buildable Qty: {{ expandedBom.estimatedBuildableQty === null ? 'Unavailable' : expandedBom.estimatedBuildableQty }}
                </span>
              </div>

              <div class="tbl-card" style="margin-bottom:0;">
                <div class="tbl-head" style="background:#fff;">
                  <span class="th" style="flex:1.3;">Component</span>
                  <span class="th" style="flex:2;">Description</span>
                  <span class="th" style="width:80px;text-align:right;">Avail</span>
                  <span class="th" style="width:90px;text-align:right;">Qty On P/O</span>
                  <span class="th" style="width:90px;text-align:right;">Req/Unit</span>
                  <span class="th" style="width:150px;">Status</span>
                </div>
                <div v-for="c in expandedBom.components" :key="c.componentCodeKey" class="tbl-row">
                  <span style="flex:1.3;font-family:monospace;font-size:11px;" class="code-pill">{{ c.componentCode }}</span>
                  <span style="flex:2;font-size:12px;">
                    {{ c.componentDescription }}
                    <div v-if="!c.inventoryRecordFound" style="font-size:10px;color:#b91c1c;">Inventory record not found</div>
                  </span>
                  <span style="width:80px;text-align:right;font-size:12px;">{{ formatQty(c.qtyAvailable) }}</span>
                  <span style="width:90px;text-align:right;font-size:12px;">{{ formatQty(c.qtyOnPurchaseOrder) }}</span>
                  <span style="width:90px;text-align:right;font-size:12px;">{{ formatQty(c.qtyRequired) }}</span>
                  <span style="width:150px;">
                    <span class="status-chip" :class="statusMeta(c.availabilityStatus).chipClass" :title="statusMeta(c.availabilityStatus).tip">
                      {{ statusMeta(c.availabilityStatus).label }}
                    </span>
                  </span>
                </div>
              </div>
            </template>

            <div v-else style="font-size:12px;color:#94a3b8;">No BOM components could be found for this item.</div>
          </div>
        </template>
      </div>

      <!-- Pagination -->
      <div class="pagination">
        <span class="page-info">Showing {{ paged.length }} of {{ totalFiltered }} filtered ({{ items.length }} total)</span>
        <div class="filter-select" style="min-width:90px;">
          <select class="native-select" v-model="pageSize">
            <option v-for="s in PAGE_SIZES" :key="s" :value="s">{{ s }} / page</option>
          </select>
        </div>
        <button class="apr-btn" :disabled="page <= 1" @click="page--">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <span class="page-info">Page {{ page }} / {{ totalPages }}</span>
        <button class="apr-btn" :disabled="page >= totalPages" @click="page++">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, h } from 'vue'
import {
  subscribeInventory,
  subscribeBom,
  hasBom,
  resolveBomForItem,
  useInventoryFilters,
  formatQty,
  formatCurrency,
  PAGE_SIZES,
} from './inventoryData'

import store from '@/store'

const canViewCost = computed(() => !!store?.state?.user?.inventoryView?.View_Inventory_Cost)

const rawItems = ref([])
const items = ref([])
const loading = ref(true)
const loadError = ref(false)
const lastRefreshed = ref(null)

let unsubInventory = null
let unsubBom = null
let bomLoadedFlag = false

function recomputeHasBom() {
  items.value = rawItems.value.map((item) => ({ ...item, hasBom: hasBom(item.itemCodeKey) }))
}

onMounted(() => {
  unsubInventory = subscribeInventory(({ items: inv, lastRefreshed: refreshedAt }) => {
    rawItems.value = inv
    lastRefreshed.value = refreshedAt
    loading.value = false
    recomputeHasBom()
  })
  unsubBom = subscribeBom(({ loaded }) => {
    bomLoadedFlag = loaded
    recomputeHasBom()
  })
})

onUnmounted(() => {
  unsubInventory?.()
  unsubBom?.()
})

function refresh() {
  loadError.value = false
  lastRefreshed.value = new Date()
}

const {
  searchTerm, filters, sort, setSort, page, pageSize,
  totalFiltered, totalPages, paged, resetFilters,
} = useInventoryFilters(items)

// BOM expansion (single row open at a time)
const expandedId = ref(null)
const expandedBom = ref(null)
const bomLoading = ref(false)

function toggleBom(item) {
  if (expandedId.value === item.id) {
    expandedId.value = null
    expandedBom.value = null
    return
  }
  expandedId.value = item.id
  bomLoading.value = !bomLoadedFlag
  expandedBom.value = resolveBomForItem(item.itemCodeKey)

  if (!bomLoadedFlag) {
    const stop = subscribeBom(({ loaded }) => {
      if (loaded) {
        expandedBom.value = resolveBomForItem(item.itemCodeKey)
        bomLoading.value = false
        stop()
      }
    })
  }
}

const STATUS_MAP = {
  available: { label: 'Available', chipClass: 'status-completed', tip: 'Enough stock available to build at least one completed item' },
  insufficient: { label: 'Insufficient Stock', chipClass: 'status-viewed', tip: 'Some stock exists but below the required quantity' },
  out_of_stock: { label: 'Out of Stock', chipClass: 'status-rejected', tip: 'No available stock for this component' },
  missing: { label: 'Record Missing', chipClass: 'status-expired', tip: 'This component could not be found in Evolution Inventory' },
}
function statusMeta(status) { return STATUS_MAP[status] || STATUS_MAP.missing }

const SortMark = {
  props: { k: String, sort: Object },
  render() {
    if (this.sort.key !== this.k) return null
    return h('span', { style: 'margin-left:3px;font-size:9px;' }, this.sort.dir === 'asc' ? '▲' : '▼')
  },
}
</script>

<style scoped>
.aq-page { background: #f1f5f9; min-height: 100vh; font-family: 'Inter', sans-serif; }

.topbar { background: #1e2d3d; padding: 14px 24px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
.topbar-left { display: flex; align-items: center; gap: 12px; }
.back-btn { background: rgba(255,255,255,0.1) !important; border: 1px solid rgba(255,255,255,0.2) !important; border-radius: 50% !important; width: 34px; height: 34px; }
.topbar-title { color: #fff; font-size: 18px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; }
.topbar-right { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.icon-btn { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.18); border-radius: 8px; display:flex; align-items:center; justify-content:center; cursor:pointer; }
.btn-save { border-radius: 8px; font-size: 12px; font-weight: 700; height: 36px; padding: 0 16px; }
.btn-pdf { background: rgba(255,255,255,0.12); color: #fff; border: 1px solid rgba(255,255,255,0.25); display:flex; align-items:center; }
.btn-pdf:hover { background: rgba(255,255,255,0.2); }

/* Option 1 applied here: Using max() and calc() for proportional expansion */
.page-content { padding: 20px 24px 48px; max-width: max(1400px, calc(1050px + 25vw)); margin: 0 auto; }

.filter-row { display: flex; gap: 10px; margin-bottom: 18px; flex-wrap: wrap; align-items: center; }
.app-input { background: #fff; border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 9px 12px; display: flex; align-items: center; gap: 7px; }
.app-input-field { background: none; border: none; outline: none; font-size: 13px; color: #1e293b; flex: 1; font-family: inherit; }
.filter-select { background: #fff; border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 8px 12px; display: flex; align-items: center; min-width: 150px; }
.native-select { background: none; border: none; outline: none; font-size: 12px; color: #64748b; flex: 1; font-family: inherit; cursor: pointer; }
.back-link { background: none; border: none; cursor: pointer; font-size: 12px; color: #64748b; display: flex; align-items: center; gap: 4px; }
.back-link:hover { color: #1e293b; }

/* Sticky Header CSS */
.main-table {
  max-height: calc(100vh - 240px); 
  overflow-y: auto;
  overflow-x: hidden;
}

.main-table .tbl-head {
  position: sticky;
  top: 0;
  z-index: 20;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05); 
}

.tbl-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; margin-bottom: 14px; }
.tbl-head { display: flex; gap: 8px; padding: 9px 16px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; align-items: center; }
.th { font-size: 10px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; color: #94a3b8; }
.sortable { cursor: pointer; user-select: none; }
.tbl-row { display: flex; gap: 8px; padding: 13px 16px; border-bottom: 1px solid #f8fafc; align-items: center; color: #1e293b; }
.tbl-row:last-child { border-bottom: none; }
.tbl-row-alt { background: #fafafa; }
.tbl-row:hover { background: #f8fafc; }
.tbl-row-approved { background: #faf5ff !important; border-left: 3px solid #7c3aed; }
.apr-btn { width: 34px; height: 34px; border-radius: 8px; border: 1.5px solid #e2e8f0; background: #f8fafc; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.apr-btn:hover { background: #f1f5f9; border-color: #94a3b8; }
.apr-btn:disabled { opacity: .4; cursor: not-allowed; }

.status-chip { font-size: 10px; padding: 3px 9px; border-radius: 20px; font-weight: 700; display: inline-block; text-transform: uppercase; letter-spacing: .04em; }
.status-completed { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; }
.status-viewed { background: #ede9fe; color: #6d28d9; border: 1px solid #ddd6fe; }
.status-rejected { background: #fee2e2; color: #b91c1c; border: 1px solid #fecaca; }
.status-expired { background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0; }

.code-pill { font-family: monospace; font-size: 10px; color: #4338ca; background: #eef2ff; padding: 2px 6px; border-radius: 4px; }
.bom-pill { font-size: 10px; color: #7c3aed; background: #ede9fe; padding: 2px 6px; border-radius: 4px; font-weight: 700; }

.revision-bar { border-radius: 8px; padding: 8px 16px; margin-bottom: 12px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; border: 1px solid; }
.rev-label { font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .07em; }
.manage-btn { color: #fff; border: none; border-radius: 7px; padding: 8px 14px; font-size: 11px; font-weight: 700; cursor: pointer; }

.empty-state { text-align: center; padding: 64px; color: #94a3b8; font-size: 14px; }
.pagination { display: flex; justify-content: center; align-items: center; gap: 10px; margin-top: 16px; }
.page-info { font-size: 12px; color: #64748b; }
</style>